import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { UniverseComponent } from 'src/domains/common/universes';
import { useCommon } from 'src/domains/common/Common';
import { useAsyncButtonAction } from 'src/domains/common/hooks/useAsyncButtonAction';
import { AppPlayersState } from 'src/domains/players/state/AppPlayers.state';
import { Popup } from 'src/domains/players/webview/ui/Popup';
import { Loader } from 'src/domains/layouts/webview/components/loader/Loader';
import {
    AcceptBtn,
    Content,
    FaqModalScroll,
    OverlayDefault,
    PopupDescription,
    PopupHeader,
    ReviewSaferGamblingBtn,
} from 'src/domains/players/webview/components/Account/Notifications/BlockingNotifications.style';

interface BlockingDepositLimitCalendarPreviewNotificationPropsType {
    onConfirm: () => Promise<void>;
}

export const BlockingDepositLimitCalendarPreviewNotification = observer(
    'BlockingDepositLimitCalendarPreviewNotification',
    ({ onConfirm }: BlockingDepositLimitCalendarPreviewNotificationPropsType): JSX.Element => {
        const common = useCommon();
        const { cmsDrawerPagesState } = AppPlayersState.get(common);
        const [isFaqOpen, setIsFaqOpen] = useState(false);
        const { handleClick: handleAcknowledge, isLoading: isAcknowledgeLoading } = useAsyncButtonAction(onConfirm);
        const { displayPage, pageType, title, content } = cmsDrawerPagesState;
        const isFaqVisible = isFaqOpen && displayPage === true && pageType === 'help';

        const openFaq = (): void => {
            setIsFaqOpen(true);
            cmsDrawerPagesState.showHelpPage();
        };

        const closeFaq = (): void => {
            setIsFaqOpen(false);
            cmsDrawerPagesState.closePage();
        };

        return (
            <OverlayDefault data-test='overlay-prompt'>
                <Content>
                    <UniverseComponent>
                        {(universeModule): React.ReactElement => <universeModule.compliancePopupLogo />}
                    </UniverseComponent>
                    <PopupHeader>
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar-preview.title'
                            defaultText='Your deposit limits are changing on 3rd November'
                        />
                    </PopupHeader>
                    <PopupDescription data-test='account.blocking-popup.deposit-limit-calendar-preview.description'>
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar-preview.description.intro'
                            defaultText='From 3rd November, your daily, weekly and monthly deposit limits will apply to calendar periods instead of rolling periods. Your deposit limit amounts will stay the same.'
                        />
                    </PopupDescription>
                    <PopupDescription>
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar-preview.description.resets'
                            defaultText='This means your limits will reset:'
                        />
                    </PopupDescription>
                    <PopupDescription>
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar-preview.description.daily'
                            defaultText='Daily: at the start of each calendar day.'
                        />
                    </PopupDescription>
                    <PopupDescription>
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar-preview.description.weekly'
                            defaultText='Weekly: at the start of each calendar week, on Monday.'
                        />
                    </PopupDescription>
                    <PopupDescription>
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar-preview.description.monthly'
                            defaultText='Monthly: on the first day of each calendar month.'
                        />
                    </PopupDescription>
                    <PopupDescription>
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar-preview.description.faq'
                            defaultText='For more information about the change and how it affects your limits, please read our Deposit Limits FAQ.'
                        />
                    </PopupDescription>
                    <AcceptBtn
                        onClick={handleAcknowledge}
                        size='medium'
                        isLoading={isAcknowledgeLoading}
                        disabled={isAcknowledgeLoading}
                        dataTest='deposit-limit-calendar-preview-acknowledge'
                    >
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar-preview.acknowledge'
                            defaultText='Acknowledge'
                        />
                    </AcceptBtn>
                    <ReviewSaferGamblingBtn
                        onClick={openFaq}
                        size='medium'
                        disabled={isAcknowledgeLoading}
                        dataTest='deposit-limit-calendar-preview-view-faq'
                    >
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar-preview.view-faq'
                            defaultText='View FAQ'
                        />
                    </ReviewSaferGamblingBtn>
                </Content>
                {isFaqVisible ? (
                    <Popup
                        title={<span dangerouslySetInnerHTML={{ __html: title ?? 'FAQ' }} />}
                        onClose={closeFaq}
                        maxWidth='500px'
                        minWidth='500px'
                        dataTest='deposit-limit-calendar-preview-faq-modal'
                    >
                        {content === null ? (
                            <Loader version='light' />
                        ) : (
                            <FaqModalScroll dangerouslySetInnerHTML={{ __html: content }} />
                        )}
                    </Popup>
                ) : null}
            </OverlayDefault>
        );
    }
);
